import React from 'react';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Select } from 'src/design/atoms/Select';
import { ResourceAttachmentDto } from 'src/api/FiretrolApiClient';

export interface IWorkflowDocumentOption {
  id: string | number;
  name?: string | null;
}

export interface IWorkflowDocumentPickerProps {
  jobId: number;
  /** Fires once per newly attached document with the temporary attachment it produced. */
  onAttached: (attachment: ResourceAttachmentDto) => void;
  options: IWorkflowDocumentOption[];
  attachFromDocument: (
    jobId: number,
    documentId: string,
  ) => Promise<ResourceAttachmentDto>;
  attaching: boolean;
}

/**
 * @registry Attaches an existing job document to a workflow record: lists document
 * options and clones the picked one into a temporary attachment on Add. Parameterize by
 * the module's document-options query and attach mutation.
 */
export const WorkflowDocumentPicker: React.FC<IWorkflowDocumentPickerProps> = ({
  jobId,
  onAttached,
  options,
  attachFromDocument,
  attaching,
}) => {
  const [selected, setSelected] = React.useState<string | null>(null);
  const [attachedIds, setAttachedIds] = React.useState<Set<string>>(new Set());

  const selectData = React.useMemo(
    () =>
      options
        .filter((o) => !attachedIds.has(String(o.id)))
        .map((option) => ({
          value: String(option.id),
          label: option.name ?? String(option.id),
        })),
    [options, attachedIds],
  );

  const handleAdd = async () => {
    if (!selected) return;
    const documentId = selected;
    const attachment = await attachFromDocument(jobId, documentId).catch(
      () => null,
    );
    if (!attachment) return;
    onAttached(attachment);
    setAttachedIds((prev) => new Set([...prev, documentId]));
    setSelected(null);
  };

  return (
    <Stack gap="xs">
      <Group align="flex-end" gap="sm" wrap="nowrap" maw={420}>
        <Select
          flex={1}
          data={selectData}
          value={selected}
          onChange={setSelected}
          label="Attach from job documents"
          description="Select a job document, then press Add to attach it."
          placeholder="Select a document"
          searchable
          clearable
        />
        <BasicButton
          onClick={handleAdd}
          loading={attaching}
          disabled={!selected}
        >
          Add
        </BasicButton>
      </Group>
      <Text size="xs" c="dimmed">
        Attached files are saved when you press Save.
      </Text>
    </Stack>
  );
};
